import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearToken, getUser, api } from '../utils/api';
import AssistantDock from './AssistantDock';
import {
  CalendarDaysIcon, NewspaperIcon, UsersIcon, UserGroupIcon, ChatBubbleLeftRightIcon,
  MapIcon, FunnelIcon, BuildingOffice2Icon, AcademicCapIcon, CubeIcon, RectangleStackIcon,
  BookOpenIcon, Squares2X2Icon, PhotoIcon, DocumentTextIcon, BriefcaseIcon, MagnifyingGlassIcon,
  ViewfinderCircleIcon, MapPinIcon, GlobeAltIcon, WrenchScrewdriverIcon, ChartBarIcon, EyeIcon,
  FlagIcon, SparklesIcon, TrophyIcon, ShieldCheckIcon, PencilSquareIcon, CheckCircleIcon,
  ClipboardDocumentCheckIcon, HomeIcon, ArrowTrendingUpIcon, HeartIcon, PresentationChartBarIcon,
  Cog6ToothIcon, UserPlusIcon, ArrowsRightLeftIcon, QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

// Ikonice za GLAVNI (tamni) nav meni — po ruti.
const MAIN_NAV_ICONS = {
  '/hbworld': BuildingOffice2Icon, '/home': HomeIcon, '/assistant': SparklesIcon, '/community': UsersIcon,
  '/news': NewspaperIcon, '/customers': BriefcaseIcon, '/explore': MagnifyingGlassIcon, '/master': AcademicCapIcon,
  '/deliver': PresentationChartBarIcon, '/grow': ArrowTrendingUpIcon, '/contribute': HeartIcon,
  '/journey': MapIcon, '/formation': AcademicCapIcon, '/my-assessments': ClipboardDocumentCheckIcon,
  '/my-class': UsersIcon, '/catalogue': BookOpenIcon, '/exchange': ArrowsRightLeftIcon, '/recruitment': UserPlusIcon,
  '/operations': Cog6ToothIcon, '/admin/process': Cog6ToothIcon, '/admin/ai-usage': Cog6ToothIcon, '/governance': ShieldCheckIcon,
  '/admin/users': UsersIcon, '/hikeadmin': MapPinIcon, '/sysadmin': Cog6ToothIcon, '/ongoing': ClipboardDocumentCheckIcon,
  '/support': QuestionMarkCircleIcon,
};
function MainNavIcon({ href, size = 18 }) {
  const Ico = MAIN_NAV_ICONS[href];
  if (!Ico) return null;
  return <Ico style={{ width: size, height: size, flexShrink: 0 }} />;
}

// Ikonice za collapsible (na +) grupne naslove — da i oni imaju ikonicu kao ostale stavke.
const GROUP_HEADER_ICONS = {
  'My Basecamp': HomeIcon, 'My Toolbox': WrenchScrewdriverIcon, 'My role — Candidate': AcademicCapIcon,
  'My role — Business Partner': BriefcaseIcon, 'My role — Administration': Cog6ToothIcon,
  'Administration': Cog6ToothIcon, 'My assignments & functions': FlagIcon,
};
function GroupHeaderIcon({ header, size = 17 }) {
  const Ico = GROUP_HEADER_ICONS[header];
  if (!Ico) return null;
  return <Ico style={{ width: size, height: size, flexShrink: 0 }} />;
}

// Ikonice za in-page podtabove (AreaLayout). Centralno po ruti — bez diranja svake stranice.
const SUBNAV_ICONS = {
  '/news/events': CalendarDaysIcon, '/news/latest': NewspaperIcon,
  '/community/chat': ChatBubbleLeftRightIcon, '/community/forums': UserGroupIcon,
  '/customers/journeys': MapIcon, '/customers/funnel': FunnelIcon, '/customers/week': CalendarDaysIcon, '/customers/clients': BuildingOffice2Icon, '/customers/find': MagnifyingGlassIcon,
  '/deliver/overview': AcademicCapIcon, '/deliver/units': CubeIcon, '/deliver/progs': RectangleStackIcon, '/deliver/logs': BookOpenIcon,
  '/deliver/mcs': Squares2X2Icon, '/deliver/fcs': PhotoIcon, '/deliver/ws': DocumentTextIcon, '/deliver/client': BriefcaseIcon, '/deliver/proposals': DocumentTextIcon,
  '/explore/overview': MagnifyingGlassIcon, '/explore/about': BuildingOffice2Icon, '/explore/compass': ViewfinderCircleIcon, '/explore/prospecting': MapPinIcon, '/explore/digital': GlobeAltIcon, '/explore/wp': DocumentTextIcon,
  '/master/overview': AcademicCapIcon, '/master/method': BookOpenIcon, '/master/profiles': ViewfinderCircleIcon, '/master/ypl': TrophyIcon,
  '/grow/atlas': WrenchScrewdriverIcon, '/grow/biz': ChartBarIcon, '/grow/expeditions': MapIcon, '/grow/feedback': ChatBubbleLeftRightIcon, '/grow/hikes': MapPinIcon,
  '/grow/obs': EyeIcon, '/grow/path': FlagIcon, '/grow/renovation': SparklesIcon, '/grow/theory': BookOpenIcon,
  '/hbworld/story': BookOpenIcon, '/hbworld/who': UsersIcon, '/hbworld/whatwedo': BriefcaseIcon, '/hbworld/code': ShieldCheckIcon, '/hbworld/oi': SparklesIcon,
  '/contribute/propose': PencilSquareIcon, '/contribute/myproposed': DocumentTextIcon, '/contribute/myapprovals': CheckCircleIcon,
  '/ongoing/tasks': ClipboardDocumentCheckIcon,
};

function SubNavIcon({ href, active }) {
  const Ico = SUBNAV_ICONS[href];
  if (!Ico) return null;
  return <Ico style={{ width: 17, height: 17, flexShrink: 0, opacity: active ? 1 : 0.7 }} />;
}

// ── Logo ───────────────────────────────────────────────────────────────────
export function Logo({ size = 'md', light }) {
  const sizes = { sm: { height: 20 }, md: { height: 80 }, lg: { height: 34 } };
  const s = sizes[size];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <img
        src="/whitetransparent.png"
        alt="HansenBeck"
        style={{ height: s.height, width: 'auto', filter: light ? 'none' : 'invert(1)' }}
      />
    </div>
  );
}

// ── useMobile hook ────────────────────────────────────────────────────────
export function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Person-oriented sidebar sekcije ───────────────────────────────────────
// Sidebar se prilagođava osobi (uniji rola i funkcija), po Basecamp mockup-u:
// Hansen Beck → My role — … (po roli) → My assignments & functions → Community.
// Faza 1: Deliver, HB World i Community/Directory su živi; ostalo su najave.
export function navSectionsFor(user) {
  const roles = user?.roles || [];
  const funcs = user?.funcs || [];
  const S = [];

  S.push({ header: 'About Hansen Beck', items: [{ href: '/hbworld', label: 'About HB & why we are here' }] });

  // My Line — the Ice Pilot's home; first item after HB World (WI-C5).
  // My Basecamp objedinjuje lični Dashboard + Community + News & Events (IP).
  if (roles.includes('IP')) {
    S.push({
      header: 'My Basecamp',
      items: [
        { href: '/home', label: 'Dashboard', tagline: 'the whole path at a glance' },
        { href: '/assistant', label: 'HB Assistant', tagline: 'your AI guide' },
        { href: '/community', label: 'Community' },
        { href: '/news', label: 'News & Events' },
      ],
    });
  }

  if (roles.includes('CAND')) {
    S.push({
      header: 'My role — Candidate',
      items: [
        { href: '/journey', label: 'My Journey' },
        { href: '/formation', label: 'My Formation' },
        { href: '/my-assessments', label: 'My Assessments' },
        { href: '/my-class', label: 'My Class' },
        { href: '/catalogue', label: 'Catalogue (teaser)' },
      ],
    });
  }
  if (roles.includes('IP')) {
    // My Customers — jedinstveno radno mesto (bivši CRM). Verbs live with the customer.
    S.push({
      header: 'My Pipeline',
      items: [
        { href: '/customers', label: 'My Pipeline', tagline: 'every customer journey, one place' },
      ],
    });

    // My Toolbox — nouns live on the Line: znanje, metod, materijali.
    S.push({
      header: 'My Toolbox',
      items: [
        { href: '/explore', label: 'Explore', tagline: 'promote & prospect — knowledge' },
        { href: '/master', label: 'Master', tagline: 'method, profiles, facet library' },
        { href: '/deliver', label: 'Deliver', tagline: 'units, programmes, materials' },
      ],
    });

    S.push({
      header: 'My Growth',
      items: [
        { href: '/grow', label: 'Grow' },
      ],
    });
    S.push({
      header: 'Making Others Great',
      items: [
        { href: '/contribute', label: 'Contribute' },
      ],
    });
  }
  if (roles.includes('BP')) {
    S.push({
      header: 'My role — Business Partner',
      items: [
        { href: '/exchange', label: 'Strategy & Projects' },
        { href: '/recruitment', label: 'Recruitment (CFA)' },
      ],
    });
  }
  if (roles.includes('ADM')) {
    S.push({
      header: 'My role — Administration', items: [
        { href: '/operations', label: 'Operations' },
        { href: '/admin/process', label: 'Sales Process Designer' },
        { href: '/admin/ai-usage', label: 'AI usage & cost' },
      ]
    });
  }
  if (roles.includes('SH')) {
    S.push({ header: 'My role — Shareholder', items: [{ href: '/governance', label: 'Governance' }, { href: '/admin/ai-usage', label: 'AI usage & cost' }] });
  }
  if (roles.includes('SUPERADMIN')) {
    S.push({
      header: 'Administration', items: [
        { href: '/admin/users', label: 'User Management' },
        { href: '/admin/process', label: 'Sales Process Designer' },
        { href: '/admin/ai-usage', label: 'AI usage & cost' },
      ]
    });
  }

  const fn = [];
  if (funcs.includes('Interviewer') && !roles.includes('BP')) fn.push({ href: '/recruitment', label: 'CFA interviews (Interviewer)' });
  if (funcs.includes('Hike administrator')) fn.push({ href: '/hikeadmin', label: 'Hike administration' });
  if (funcs.includes('System Administrator')) fn.push({ href: '/sysadmin', label: 'System administration' });
  if (funcs.includes('CFA Leader')) fn.push({ href: '/cfalead', label: 'CFA leadership' });
  if (funcs.includes('Ice Pilot Growth Leader')) fn.push({ href: '/growthlead', label: 'Ice Pilot Growth leadership' });
  if (fn.length) S.push({ header: 'My assignments & functions', items: fn });

  // Community i News & Events: za IP su premešteni pod My Basecamp; korisnici
  // bez IP role i dalje ih vide kao zasebne stavke.
  if (!roles.includes('IP')) {
    S.push({
      header: 'Community',
      items: [{ href: '/community', label: 'Community' }]
    });
    S.push({
      header: 'News & Events',
      items: [{ href: '/news', label: 'News & Events' }]
    });
  }

  // Ongoing Things — za IP je ugrađen u Dashboard; ostali ga vide u donjoj navigaciji.
  if (!roles.includes('IP')) {
    S.push({
      header: 'Ongoing Things',
      items: [{ href: '/ongoing', label: 'Ongoing Things' }]
    });
  }

  // Support — how to use Basecamp + FAQ, for everyone.
  S.push({ header: 'Support', items: [{ href: '/support', label: 'Support & FAQ' }] });
  return S;
}

// ── Basecamp layout (crni sidebar + content) ──────────────────────────────
export function BasecampLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUserState] = useState(() => getUser());
  const [openSection, setOpenSection] = useState(null); // accordion: only one section open
  const isMobile = useMobile();

  useEffect(() => {
    api.getMe().then(d => setUserState(d?.user || d)).catch(() => { });
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  function handleLogout() {
    clearToken();
    navigate('/login');
  }

  const sections = navSectionsFor(user);
  const fullName = user?.name || '';
  const roles = user?.roles || [];
  const funcs = user?.funcs || [];

  // The section containing the current route — opened by default so the active page is reachable.
  const activeHeader = (() => {
    for (const s of sections) {
      if (s.items.some(it => location.pathname === it.href || location.pathname.startsWith(it.href + '/'))) return s.header;
    }
    return null;
  })();
  useEffect(() => { if (activeHeader) setOpenSection(activeHeader); }, [activeHeader]);

  const sidebarWidth = isMobile ? 260 : (collapsed ? 56 : 240);
  const iconOnly = collapsed && !isMobile;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--canvas)' }}>

      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 99, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: sidebarWidth, flexShrink: 0,
        background: 'var(--ink)', display: 'flex', flexDirection: 'column',
        transition: 'width var(--transition), transform var(--transition)',
        overflow: 'hidden',
        ...(isMobile ? {
          position: 'fixed', left: 0, top: 0, bottom: 0,
          zIndex: 100,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        } : {
          position: 'sticky', top: 0, height: '100vh',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }),
      }}>
        {/* Logo area */}
        <div style={{ padding: iconOnly ? '18px 12px' : '22px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', minHeight: 64, display: 'flex', alignItems: 'center' }}>
          {iconOnly ? (
            <img src="/whitetransparent.png" alt="HB" style={{ height: 18, width: 'auto' }} />
          ) : (
            <div>
              <Logo light size="lg" />
              <div style={{ fontSize: '0.62rem', letterSpacing: '0.22em', color: 'rgba(255,255,255,0.4)', marginTop: 6, textTransform: 'uppercase' }}>
                Basecamp
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px', overflowY: 'auto' }}>
          {sections.map((section, si) => {
            const isSingleItem = section.items.length === 1;
            const singleItem = isSingleItem ? section.items[0] : null;
            const sectionOpen = openSection === section.header;
            if (isSingleItem) {
              const active = location.pathname === singleItem.href || location.pathname.startsWith(singleItem.href + '/');

              return (
                <Link
                  key={section.header}
                  to={singleItem.href}
                  onClick={() => isMobile && setMobileOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, justifyContent: iconOnly ? 'center' : 'flex-start',
                    padding: iconOnly ? '10px 8px' : '9px 10px',
                    marginTop: si === 0 ? 2 : 6,
                    borderRadius: 'var(--radius-sm)',
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                    fontSize: iconOnly ? '0.88rem' : '0.72rem',
                    fontWeight: 700,
                    letterSpacing: iconOnly ? 'normal' : '0.1em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    borderLeft: active ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent',
                    transition: 'all var(--transition)',
                  }}
                >
                  {!iconOnly ? (
                    <>
                      <MainNavIcon href={singleItem.href} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {section.header}
                      </span>
                    </>
                  ) : (
                    MAIN_NAV_ICONS[singleItem.href] ? <MainNavIcon href={singleItem.href} /> : <span>{section.header.charAt(0)}</span>
                  )}
                </Link>
              );
            }
            return (
              <React.Fragment key={section.header}>
                {!iconOnly && (
                  <button
                    onClick={() => setOpenSection(sectionOpen ? null : section.header)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                      padding: '9px 10px', marginTop: si === 0 ? 2 : 6,
                      background: sectionOpen ? 'rgba(255,255,255,0.05)' : 'none', border: 'none', cursor: 'pointer',
                      borderRadius: 'var(--radius-sm)', textAlign: 'left', fontFamily: 'var(--font-body)',
                      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.7)',
                      transition: 'color var(--transition), background var(--transition)',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <GroupHeaderIcon header={section.header} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.header}</span>
                    </span>
                    <span style={{ fontSize: '1rem', lineHeight: 1, color: 'rgba(255,255,255,0.4)', flexShrink: 0, marginLeft: 8 }}>{sectionOpen ? '−' : '+'}</span>
                  </button>
                )}
                {(iconOnly || sectionOpen) && section.items.map((item, ii) => {
                  const active = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                  const isLast = ii === section.items.length - 1;
                  return (
                    <div key={item.href + item.label} style={{ position: 'relative', marginLeft: iconOnly ? 0 : 18, paddingLeft: iconOnly ? 0 : 14 }}>
                    {/* Tree connector — vertical line from the group header, elbow into each sub-item */}
                    {!iconOnly && (
                      <>
                        <span style={{ position: 'absolute', left: 0, top: 0, bottom: isLast ? '50%' : 0, borderLeft: '1px solid rgba(255,255,255,0.22)' }} />
                        <span style={{ position: 'absolute', left: 0, top: '50%', width: 10, borderTop: '1px solid rgba(255,255,255,0.22)' }} />
                      </>
                    )}
                    <Link
                      to={item.href}
                      onClick={() => isMobile && setMobileOpen(false)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: iconOnly ? '10px 8px' : '8px 10px',
                        borderRadius: 'var(--radius-sm)',
                        background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                        color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                        fontSize: '0.88rem', fontWeight: active ? 600 : 400,
                        transition: 'all var(--transition)',
                        minWidth: 0,
                        borderLeft: active ? '2px solid rgba(255,255,255,0.6)' : '2px solid transparent',
                        justifyContent: iconOnly ? 'center' : 'flex-start',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {!iconOnly ? (
                        <>
                          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0, gap: '1px' }}>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                            {item.tagline && (
                              <span style={{ fontSize: '0.66rem', fontWeight: 400, color: 'rgba(255,255,255,0.35)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.tagline}
                              </span>
                            )}
                          </span>
                        </>
                      ) : (
                        MAIN_NAV_ICONS[item.href] ? <MainNavIcon href={item.href} /> : <span>{item.label.charAt(0)}</span>
                      )}
                    </Link>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {(!collapsed || isMobile) && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '6px',
              padding: '10px 10px', borderRadius: 'var(--radius-sm)',
              background: 'rgba(255,255,255,0.06)',
            }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fullName}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {roles.map(r => (
                  <span key={r} style={{ fontSize: '0.62rem', padding: '1px 7px', borderRadius: '99px', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)' }}>{r}</span>
                ))}
                {funcs.map(f => (
                  <span key={f} style={{ fontSize: '0.62rem', padding: '1px 7px', borderRadius: '99px', border: '1px dashed rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.55)' }}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Collapse toggle — desktop only */}
          {!isMobile && (
            <button onClick={() => setCollapsed(!collapsed)} style={{
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '8px', padding: '7px 10px', borderRadius: 'var(--radius-sm)',
              color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', background: 'none', border: 'none',
              cursor: 'pointer', transition: 'color var(--transition)', fontFamily: 'var(--font-body)',
            }}>
              <span style={{ fontSize: '0.9rem' }}>{collapsed ? '→' : '←'}</span>
              {!collapsed && 'Collapse'}
            </button>
          )}

          {/* Logout */}
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', justifyContent: iconOnly ? 'center' : 'flex-start',
            gap: '8px', padding: '8px 10px', borderRadius: 'var(--radius-sm)',
            color: 'rgba(255,100,100,0.75)', fontSize: '0.82rem', fontWeight: 500,
            background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.15)',
            cursor: 'pointer', transition: 'all var(--transition)', fontFamily: 'var(--font-body)',
          }}>
            <span style={{ fontSize: '0.95rem' }}>↩</span>
            {(!collapsed || isMobile) && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 52,
          background: 'var(--ink)', display: 'flex', alignItems: 'center',
          padding: '0 16px', gap: '14px', zIndex: 98,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <button
            onClick={() => setMobileOpen(o => !o)}
            style={{ color: '#fff', padding: '6px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '5px' }}
            aria-label="Toggle menu"
          >
            <span style={{ width: 20, height: 2, background: '#fff', display: 'block' }} />
            <span style={{ width: 20, height: 2, background: '#fff', display: 'block' }} />
            <span style={{ width: 20, height: 2, background: '#fff', display: 'block' }} />
          </button>
          <Logo light size="sm" />
        </div>
      )}

      {/* Main content — NOTE: no overflow here. overflow-x:auto would make main a scroll
          container (overflow-y computes to auto) and break `position: sticky` submenus.
          Wide content (tables, etc.) manages its own horizontal scroll. */}
      <main style={{
        flex: 1, minWidth: 0, background: 'var(--page)',
        padding: isMobile ? '68px 16px 32px' : '36px 40px',
      }}>
        {children}
      </main>

      {/* HB Assistant — always reachable as a popup on the right */}
      <AssistantDock />
    </div>
  );
}

// ── Area layout — content sa submenijem podsekcija (mockup .subnav obrazac) ─
export function AreaLayout({ items, children }) {
  const location = useLocation();
  const isMobile = useMobile();

  // Collapse state po headeru — svaki header kontroliše SAMO svoje podsekcije.
  // (Ranije je jedan boolean otvarao/zatvarao sve headere odjednom.)
  const [openHeaders, setOpenHeaders] = useState({});

  // Prati tekući header dok iteriramo — svaka uvučena podsekcija pripada
  // poslednjem headeru iznad sebe.
  let currentHeader = null;

  // Flat sub-menu (no headers / indents) = pill tabs across the top, as in the HB Sales Tool visual contract.
  const flat = items.every(it => !it.header && !it.indent && !it.isHeaderLink);
  if (flat) {
    return (
      <div>
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {items.map(it => {
            const isActive = location.pathname === it.href || location.pathname.startsWith(it.href + '/');
            return (
              <Link key={it.href} to={it.href} style={{
                padding: '9px 16px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 700,
                border: `1px solid ${isActive ? 'var(--ink)' : 'var(--line)'}`,
                background: isActive ? 'var(--ink)' : 'var(--canvas-white)',
                color: isActive ? '#fff' : 'var(--ink)',
                textDecoration: 'none', transition: 'all var(--transition)', whiteSpace: 'nowrap',
              }}>
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div style={{ minWidth: 0 }}>{children}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
      <div style={{
        width: isMobile ? '100%' : 210, flexShrink: 0,
        display: 'flex', flexDirection: isMobile ? 'row' : 'column', flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: '2px', position: isMobile ? 'static' : 'sticky', top: 36,
        background: 'var(--canvas-white)', border: isMobile ? 'none' : 'var(--panel-border)', borderRadius: 'var(--radius-panel)', padding: isMobile ? 0 : 8,
      }}>
        {items.map(it => {
          // 1. Običan header koji ima drop-down (Market Development)
          if (it.header) {
            currentHeader = it.header;
            const open = !!openHeaders[it.header];
            return (
              <button
                key={it.header}
                onClick={() => setOpenHeaders(s => ({ ...s, [it.header]: !s[it.header] }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 10px 4px', fontSize: '0.68rem', fontWeight: 700,
                  letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)',
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontFamily: 'var(--font-body)',
                }}
              >
                <span>{it.header}</span>
                <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>{open ? '−' : '+'}</span>
              </button>
            );
          }

          // Ako je matični header skupljen, preskačemo uvučene podsekcije tog headera
          if (it.indent && currentHeader && !openHeaders[currentHeader]) {
            return null;
          }

          const isActive = location.pathname === it.href || location.pathname.startsWith(it.href + '/');

          // 2. Naslov koji je direktan link (Ice Pilot as an Explorer)
          if (it.isHeaderLink) {
            return (
              <Link
                key={it.href}
                to={it.href}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '9px 10px', // Ujednačen gore/dole padding da tekst bude u centru okvira
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: isActive ? 'var(--ink)' : 'var(--ink-faint)',
                  background: isActive ? 'var(--canvas-warm)' : 'transparent',
                  transition: 'all var(--transition)',
                  textDecoration: 'none',
                  marginTop: 2, // Blagi razmak od vrha radi simetrije sa dugmićima
                  gap: 9,
                }}
              >
                <SubNavIcon href={it.href} active={isActive} />
                {it.label}
              </Link>
            );
          }

          // 3. Uvučene podsekcije (Presenting HB, Compass, Prospecting, Digital, White Papers)
          return (
            <Link
              key={it.href}
              to={it.href}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.85rem',
                background: isActive ? 'var(--ink)' : 'transparent',
                color: isActive ? '#fff' : 'var(--body-ink)',
                fontWeight: isActive ? 700 : 400,
                transition: 'all var(--transition)',
                textDecoration: 'none',
              }}
            >
              <SubNavIcon href={it.href} active={isActive} />
              {it.label}
            </Link>
          );
        })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

// ── Auth layout ────────────────────────────────────────────────────────────
export function AuthLayout({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'var(--ink)', padding: '24px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, opacity: 0.08,
        backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
        backgroundSize: '40px 40px', pointerEvents: 'none',
      }} />
      <div style={{
        background: 'var(--canvas-white)', borderRadius: 'var(--radius-lg)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', width: '100%', maxWidth: '400px',
        padding: '36px', position: 'relative',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid var(--line)' }}>
          <img src="/logowhite.png" alt="HansenBeck" style={{ height: 56, width: 'auto', marginBottom: '10px' }} />
          <div style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', fontWeight: 600 }}>
            HB Basecamp
          </div>
        </div>
        {children}
        <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--line)' }}>
          <Link to="/" style={{ fontSize: '0.82rem', color: 'var(--ink-faint)', textDecoration: 'none', letterSpacing: '0.01em' }}>
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
